import { useDiscussFlag } from '../mutations/useDiscussFlag.js'
import { useMetaData } from '../queries/useMetaData.js'
import { useGoalsData } from '../queries/useGoalsData.js'
import { useIsReadOnly } from '../store/archiveViewStore.js'
import { goalKey, pillarIndexByName } from '../data/metaKeys.js'

/**
 * DiscussFlagButton — small ⚑ chip that flags a goal for THIS week's
 * leadership review. Anyone can flip it (not gated to DRI). Resets every
 * Wednesday wipe.
 *
 * Two visual modes:
 *   - flagged → solid amber pill
 *   - unflagged → muted outline pill (only visible on hover via parent
 *     when used in dense rows; stays visible inline elsewhere)
 *
 * Reads from meta.discussFlags[goalKey]. Writes via /meta/discussFlags
 * partial-update endpoint, optimistic-cache then server-authoritative.
 */
export default function DiscussFlagButton({ pillarName, goalIndex, size = 'sm' }) {
  const readOnly = useIsReadOnly()
  const { data: meta } = useMetaData()
  const { data: goalsData } = useGoalsData()
  const flagOp = useDiscussFlag()

  const pi = pillarIndexByName(goalsData, pillarName)
  const key = pi >= 0 ? goalKey(pi, goalIndex) : null
  const flagged = key ? Boolean(meta?.discussFlags?.[key]) : false

  if (!key) return null

  const onClick = (e) => {
    e.stopPropagation()
    if (readOnly) return
    flagOp.mutate({ goalKey: key, value: !flagged })
  }

  const dims = size === 'lg'
    ? { padX: 8, padY: 3, font: 11, icon: 11 }
    : { padX: 6, padY: 2, font: 10, icon: 10 }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={readOnly}
      title={
        readOnly
          ? 'Flags are read-only in archive view'
          : flagged
            ? 'Flagged for this week — click to unflag'
            : 'Flag this goal for discussion this week'
      }
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: `${dims.padY}px ${dims.padX}px`,
        borderRadius: 99,
        background: flagged ? '#fef3c7' : 'transparent',
        color: flagged ? '#854d0e' : 'var(--muted-light)',
        border: `1px solid ${flagged ? '#fbbf24' : 'var(--border)'}`,
        fontSize: dims.font,
        fontWeight: 600,
        cursor: readOnly ? 'default' : 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontSize: dims.icon, lineHeight: 1 }}>⚑</span>
      {flagged ? 'flagged' : 'discuss'}
    </button>
  )
}
